import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import TYPES from '../constants/types';
import { AppointmentReportPdfInput } from '../types/appointment-report';

type PdfMakeInstance = {
  vfs?: Record<string, string>;
  fonts?: Record<string, { normal: string; bold: string; italics: string; bolditalics: string }>;
  createPdf: (docDefinition: Record<string, unknown>) => {
    getBlob: (callback: (blob: Blob) => void) => void;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AppointmentsReportPdfService {
  private translate: TranslateService = inject(TranslateService);
  private logoDataUrlPromise: Promise<string | null> | null = null;

  async generateReportPdfBlob(input: AppointmentReportPdfInput): Promise<Blob> {
    const pdfMake = await this.loadPdfMake();
    const logoDataUrl = await this.getLogoDataUrl();
    const docDefinition = this.buildDocDefinition(input, logoDataUrl);

    return new Promise<Blob>((resolve, reject) => {
      try{
        pdfMake.createPdf(docDefinition).getBlob((blob) => resolve(blob));
      }catch(error){
        reject(error);
      }
    });
  }

  private async loadPdfMake(): Promise<PdfMakeInstance> {
    const [pdfMakeModule, pdfFontsModule] = await Promise.all([
      import('pdfmake/build/pdfmake'),
      import('pdfmake/build/vfs_fonts')
    ]);

    const pdfMake = ((pdfMakeModule as any).default ?? pdfMakeModule) as PdfMakeInstance;
    const pdfFonts = (pdfFontsModule as any).default ?? pdfFontsModule;
    const vfs = this.resolveVfs(pdfFonts);

    if(vfs && Object.keys(vfs).length > 0){
      pdfMake.vfs = vfs;
      pdfMake.fonts = {
        Roboto: {
          normal: 'Roboto-Regular.ttf',
          bold: 'Roboto-Medium.ttf',
          italics: 'Roboto-Italic.ttf',
          bolditalics: 'Roboto-MediumItalic.ttf'
        }
      };
    }

    return pdfMake;
  }

  private resolveVfs(pdfFonts: any): Record<string, string> {
    if(pdfFonts?.pdfMake?.vfs){
      return pdfFonts.pdfMake.vfs as Record<string, string>;
    }

    if(pdfFonts?.vfs){
      return pdfFonts.vfs as Record<string, string>;
    }

    // pdfmake >=0.2 can export a flat map where font filenames are top-level keys.
    if(pdfFonts && typeof pdfFonts === 'object' && 'Roboto-Regular.ttf' in pdfFonts){
      return pdfFonts as Record<string, string>;
    }

    return {};
  }

  private buildDocDefinition(input: AppointmentReportPdfInput, logoDataUrl: string | null = null): Record<string, unknown> {
    const { meta, lines, totals } = input;
    const palette = {
      primary: '#4F378B',
      primaryLight: '#BFA2DB',
      background: '#F9F9F9',
      white: '#FFFFFF',
      gray: '#757575',
      lightGray: '#E6E6E6',
      black: '#000000'
    };

    const dateTimeHeader = this.translate.instant('appointments-report.date_time');
    const currencyHeader = this.translate.instant('appointments-report.currency');
    const amountHeader = this.translate.instant('appointments-report.amount');

    const appointmentsHeader = this.translate.instant('appointments-report.appointments');
    const totalsHeader = this.translate.instant('appointments-report.total_per_currency');
    const commentsHeader = this.translate.instant('appointments-report.comments');
    const totalSessionsLabel = this.translate.instant('appointments-report.total_sessions');
    const generatedByLabel = this.translate.instant('appointments-report.generated_by');
    const emptyLabel = this.translate.instant('appointments-report.empty');
    const commentsText = meta.comments?.trim() ?? '';
    const hasComments = !!commentsText;

    const appointmentRows: Array<Array<Record<string, unknown>>> = lines.length > 0
      ? lines.map((line) => [
        {
          text: `${this.formatDate(line.appointmentDate)}\n${this.formatTime(line.appointmentDate)}`,
          style: 'tableCell'
        },
        { text: line.currency || '-', style: 'tableCellCenter' },
        {
          text: this.formatAmount(line.currency, line.value),
          style: 'tableCellRight'
        }
      ])
      : [[
        {
          text: emptyLabel,
          colSpan: 3,
          style: 'tableCellMuted'
        },
        {},
        {}
      ]];

    const summaryRows: Array<Array<Record<string, unknown>>> = [
      [
        { text: totalSessionsLabel, style: 'summaryLabel' },
        { text: String(lines.length), style: 'summaryValue' }
      ],
      ...totals.map((item) => ([
        { text: item.currency || '-', style: 'summaryLabel' },
        { text: this.formatAmount(item.currency, item.total), style: 'summaryValue' }
      ]))
    ];

    const logoCell: Record<string, unknown> = logoDataUrl
      ? {
        stack: [
          {
            image: logoDataUrl,
            width: 64,
            height: 64,
            alignment: 'center',
            margin: [0, 0, 0, 0]
          }
        ],
        alignment: 'center',
        margin: [0, 0, 0, 0]
      }
      : { text: '' };

    return {
      pageSize: 'A4',
      pageMargins: [26, 28, 26, 28],
      background: () => ({
        canvas: [
          { type: 'rect', x: 0, y: 0, w: 595.28, h: 841.89, color: palette.background }
        ]
      }),
      defaultStyle: {
        fontSize: 11,
        color: palette.black
      },
      content: [
        {
          table: {
            widths: [66, '*'],
            body: [[
              logoCell,
              {
                stack: [
                  { text: meta.reportName || this.translate.instant('appointments-report.title'), style: 'headerTitle' },
                  { text: meta.patientName || '-', style: 'headerPatient' },
                  { text: `${this.formatDate(meta.startDate)} - ${this.formatDate(meta.endDate)}`, style: 'headerRange' }
                ],
                margin: [0, 2, 0, 0]
              }
            ]]
          },
          layout: this.cardLayout(palette.primaryLight),
          margin: [0, 0, 0, 12]
        },
        { text: appointmentsHeader, style: 'sectionTitle', margin: [0, 14, 0, 8] },
        {
          table: {
            headerRows: 1,
            widths: ['40%', '28%', '32%'],
            body: [
              [
                { text: dateTimeHeader, style: 'tableHeader' },
                { text: currencyHeader, style: 'tableHeaderCenter' },
                { text: amountHeader, style: 'tableHeaderRight' }
              ],
              ...appointmentRows
            ]
          },
          layout: this.sectionTableLayout(palette.primaryLight, palette.lightGray),
          margin: [0, 0, 0, 12]
        },
        { text: totalsHeader, style: 'sectionTitle', margin: [0, 2, 0, 8] },
        {
          table: {
            widths: ['*', 'auto'],
            body: summaryRows
          },
          layout: this.sectionTableLayout(palette.primaryLight, palette.lightGray),
          margin: [0, 0, 0, 16]
        },
        ...(hasComments ? [
          { text: commentsHeader, style: 'sectionTitle', margin: [0, 0, 0, 8] },
          {
            table: {
              widths: ['*'],
              body: [[{ text: commentsText, style: 'commentsText' }]]
            },
            layout: this.cardLayout(palette.primaryLight),
            margin: [0, 0, 0, 16]
          }
        ] : []),
        {
          unbreakable: true,
          stack: [
            { text: generatedByLabel, style: 'sectionTitleCentered', margin: [0, 0, 0, 6] },
            {
              text: this.buildPsychologistLine(meta.psychologistName, meta.psychologistLicenseNumber, meta.psychologistLicenseCountry),
              style: 'footerLine',
              margin: [0, 0, 0, 3]
            },
            { text: meta.psychologistEmail || '-', style: 'footerLine', margin: [0, 0, 0, 3] },
            { text: this.formatDateTime(meta.generatedAt), style: 'footerLine' }
          ]
        }
      ],
      styles: {
        headerTitle: {
          color: palette.primary,
          fontSize: 14,
          bold: true
        },
        headerPatient: {
          color: palette.black,
          bold: true,
          margin: [0, 4, 0, 0]
        },
        headerRange: {
          color: palette.gray,
          margin: [0, 3, 0, 0]
        },
        sectionTitle: {
          color: palette.primary,
          bold: true,
          fontSize: 14
        },
        sectionTitleCentered: {
          color: palette.primary,
          bold: true,
          fontSize: 14,
          alignment: 'center'
        },
        tableHeader: {
          color: palette.primary,
          bold: true
        },
        tableHeaderCenter: {
          color: palette.primary,
          bold: true,
          alignment: 'center'
        },
        tableHeaderRight: {
          color: palette.primary,
          bold: true,
          alignment: 'right'
        },
        tableCell: {
          color: palette.black
        },
        tableCellCenter: {
          color: palette.black,
          alignment: 'center'
        },
        tableCellRight: {
          color: palette.black,
          alignment: 'right'
        },
        tableCellMuted: {
          color: palette.gray,
          italics: true
        },
        summaryLabel: {
          color: palette.black
        },
        summaryValue: {
          color: palette.black,
          alignment: 'right',
          bold: true
        },
        commentsText: {
          color: palette.black,
          lineHeight: 1.35
        },
        footerLine: {
          color: palette.gray,
          alignment: 'center',
          lineHeight: 1.2
        }
      }
    };
  }

  private cardLayout(borderColor: string): Record<string, (index: number, node: any) => number | string> {
    return {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => borderColor,
      vLineColor: () => borderColor,
      fillColor: () => '#FFFFFF',
      paddingLeft: () => 10,
      paddingRight: () => 10,
      paddingTop: () => 10,
      paddingBottom: () => 10
    };
  }

  private sectionTableLayout(borderColor: string, rowColor: string): Record<string, (index: number, node: any) => number | string> {
    return {
      hLineWidth: () => 1,
      vLineWidth: (i: number, node: any) => (i === 0 || i === node.table.widths.length ? 1 : 0),
      hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length ? borderColor : rowColor),
      vLineColor: () => borderColor,
      fillColor: () => '#FFFFFF',
      paddingLeft: () => 10,
      paddingRight: () => 10,
      paddingTop: () => 8,
      paddingBottom: () => 8
    };
  }

  private async getLogoDataUrl(): Promise<string | null> {
    if(!this.logoDataUrlPromise){
      this.logoDataUrlPromise = this.loadLogoDataUrl();
    }

    return this.logoDataUrlPromise;
  }

  private async loadLogoDataUrl(): Promise<string | null> {
    try{
      const response = await fetch('/imgs/icon.png');
      if(!response.ok){
        return null;
      }

      const blob = await response.blob();
      return await this.blobToDataUrl(blob);
    }catch{
      return null;
    }
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if(typeof result === 'string'){
          resolve(result);
          return;
        }
        reject(new Error('Could not convert blob to data URL.'));
      };
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed.'));
      reader.readAsDataURL(blob);
    });
  }

  private formatAmount(currencyCode: string, amount: number): string {
    const language = this.translate.currentLang || 'en';
    const formatter = new Intl.NumberFormat(language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const symbol = this.getCurrencySymbol(currencyCode);
    const value = formatter.format(Number.isFinite(amount) ? amount : 0);
    return symbol ? `${symbol} ${value}` : value;
  }

  private formatDate(dateInput: Date | string): string {
    const date = new Date(dateInput);

    if(Number.isNaN(date.getTime())){
      return '-';
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${month}/${year}`;
  }

  private formatTime(dateInput: Date | string): string {
    const date = new Date(dateInput);

    if(Number.isNaN(date.getTime())){
      return '--:--';
    }

    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');
    return `${hour}:${minute}`;
  }

  private formatDateTime(dateInput: Date | string): string {
    return `${this.formatDate(dateInput)} ${this.formatTime(dateInput)}`;
  }

  private buildPsychologistLine(name: string, licenseNumber: string, licenseCountry: string): string {
    const psychologistName = name || '-';
    const license = `${licenseNumber || ''} ${licenseCountry || ''}`.trim();
    return license ? `${psychologistName} - ${license}` : psychologistName;
  }

  private getCurrencySymbol(code: string): string {
    const currency = TYPES.DATABASE.CURRENCY.find((item: any) => item.code === code);
    return currency ? currency.symbol : '';
  }
}
